import {
  addManualMealPlanItem,
  updateManualMealPlanServings,
  updateDraft,
  updateNutritionServings,
  updateRecipeCustomizations,
} from '@/lib/api/mealPlans';
import { showWarningToast } from '@/utils/toast-utils';
import type { ExpandedFoodRow, FoodSearchResult, QuantitySyncField } from './nutritionalAnalysisTypes';

type AIDraftRecipeContext = { mealOrder: number; recipeId: string } | null;

type AIInlineCommon = {
  resolvedPlanId: string;
  selectedDayNumber: number;
  mealName: string;
  isDraftGenerationPending: boolean;
  resolveMealApiName: (mealName: string) => string;
};

export async function runAIDraftFoodSelection(params: AIInlineCommon & {
  selectedItem: FoodSearchResult;
  selectedId: string;
  provider: 'spoonacular' | 'internal';
  source: 'cached' | 'api';
  defaultAddIngredientServingId: string;
  defaultAddRecipeServingId: string;
  preferredIngredientServingId?: string;
}): Promise<void> {
  const {
    resolvedPlanId,
    selectedDayNumber,
    mealName,
    isDraftGenerationPending,
    selectedItem,
    selectedId,
    provider,
    source,
    defaultAddIngredientServingId,
    defaultAddRecipeServingId,
    preferredIngredientServingId,
    resolveMealApiName,
  } = params;
  const ingredientServingId = String(preferredIngredientServingId ?? '').trim() || defaultAddIngredientServingId;

  if (isDraftGenerationPending) {
    showWarningToast(
      'Draft is still generating',
      'Please wait for generation to complete before making changes.',
    );
    return;
  }

  const response = await addManualMealPlanItem(
    selectedItem.type === 'ingredient'
      ? {
          draftId: resolvedPlanId,
          day: selectedDayNumber,
          mealName: resolveMealApiName(mealName),
          itemType: 'ingredient',
          ingredient: {
            id: selectedId,
            provider,
            amount: 1,
            servingId: ingredientServingId,
          },
        }
      : {
          draftId: resolvedPlanId,
          day: selectedDayNumber,
          mealName: resolveMealApiName(mealName),
          itemType: 'recipe',
          recipe: {
            id: selectedId,
            provider,
            source,
          },
          nutritionServings: 1,
          portionSizeMultiplier: 1,
          selectedServingId: defaultAddRecipeServingId,
        },
  );
  if (!response.success) {
    throw new Error(response.message || 'Failed to add food item');
  }
}

export async function runAIDraftIngredientSelection(params: AIInlineCommon & {
  selectedId: string;
  provider: 'spoonacular' | 'internal';
  defaultAddIngredientServingId: string;
  preferredIngredientServingId?: string;
}): Promise<void> {
  const {
    resolvedPlanId,
    selectedDayNumber,
    mealName,
    isDraftGenerationPending,
    selectedId,
    provider,
    defaultAddIngredientServingId,
    preferredIngredientServingId,
    resolveMealApiName,
  } = params;
  const ingredientServingId = String(preferredIngredientServingId ?? '').trim() || defaultAddIngredientServingId;

  if (isDraftGenerationPending) {
    showWarningToast(
      'Draft is still generating',
      'Please wait for generation to complete before making changes.',
    );
    return;
  }

  if (!selectedId) {
    showWarningToast('Ingredient id missing', 'Please choose a valid ingredient option.');
    return;
  }

  const response = await addManualMealPlanItem({
    draftId: resolvedPlanId,
    day: selectedDayNumber,
    mealName: resolveMealApiName(mealName),
    itemType: 'ingredient',
    ingredient: {
      id: selectedId,
      provider,
      amount: 1,
      servingId: ingredientServingId,
    },
  });
  if (!response.success) {
    throw new Error(response.message || 'Failed to add ingredient');
  }
}

export async function runAIDraftQuantitySync(params: {
  resolvedPlanId: string;
  selectedDayNumber: number;
  mealName: string;
  targetRow: ExpandedFoodRow;
  quantity: number;
  isDraftGenerationPending: boolean;
  resolveAIDraftRecipeContext: (mealName: string, targetRow?: ExpandedFoodRow) => AIDraftRecipeContext;
  resolveMealApiName: (mealName: string) => string;
  parseRecipeServingId: (value: unknown) => string | undefined;
  defaultAIDraftIngredientServingId: string;
  isIngredientRow: (row: Pick<ExpandedFoodRow, 'itemType' | 'ingredientId'>) => boolean;
  syncField?: QuantitySyncField;
}): Promise<void> {
  const {
    resolvedPlanId,
    selectedDayNumber,
    mealName,
    targetRow,
    quantity,
    isDraftGenerationPending,
    resolveAIDraftRecipeContext,
    resolveMealApiName,
    parseRecipeServingId,
    defaultAIDraftIngredientServingId,
    isIngredientRow,
    syncField,
  } = params;

  if (isDraftGenerationPending) {
    showWarningToast(
      'Draft is still generating',
      'Please wait for generation to complete before editing ingredients or servings.',
    );
    return;
  }

  const safeQuantity = quantity > 0 ? quantity : 0.1;
  const ingredientRow = isIngredientRow(targetRow);

  if (!ingredientRow) {
    const normalizedItemId = String(targetRow.itemId ?? '').trim();
    const normalizedRecipeId = String(targetRow.recipeId ?? targetRow.itemId ?? '').trim();
    const normalizedSource = String(targetRow.source ?? '').trim().toLowerCase();
    const selectedServingId = parseRecipeServingId(targetRow.serving);
    const isImportedRecipeRow =
      targetRow.fromManualSlotItem === true
      || normalizedSource === 'internal'
      || Boolean(normalizedItemId && normalizedRecipeId && normalizedItemId !== normalizedRecipeId);

    // Imported recipes in AI-draft flow are persisted through manual-draft endpoints.
    if (isImportedRecipeRow && normalizedRecipeId) {
      const response = await updateManualMealPlanServings({
        draftId: resolvedPlanId,
        day: selectedDayNumber,
        mealName: resolveMealApiName(mealName),
        itemId: normalizedItemId || normalizedRecipeId,
        recipeId: normalizedRecipeId,
        nutritionServings: safeQuantity,
        servings: safeQuantity,
        selectedServingId,
      });
      if (!response.success) {
        throw new Error(response.message || 'Failed to update imported recipe servings');
      }
      return;
    }
  }

  const draftRecipeContext = resolveAIDraftRecipeContext(mealName, targetRow);
  if (!draftRecipeContext) {
    showWarningToast('Recipe context missing', 'Unable to map this row to the selected draft recipe.');
    return;
  }

  if (ingredientRow) {
    const originalIngredientId = String(targetRow.ingredientId ?? targetRow.itemId ?? '').trim();
    if (!originalIngredientId) return;

    const response = await updateRecipeCustomizations(
      resolvedPlanId,
      selectedDayNumber,
      draftRecipeContext.mealOrder,
      draftRecipeContext.recipeId,
      {
        recipeId: draftRecipeContext.recipeId,
        source: 'spoonacular',
        modifications: [
          {
            type: 'modify',
            originalIngredientId,
            amount: safeQuantity,
            unit: String(targetRow.unit ?? '').trim() || defaultAIDraftIngredientServingId,
          },
        ],
      },
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to update ingredient');
    }
    return;
  }

  const response = await updateNutritionServings(
    resolvedPlanId,
    selectedDayNumber,
    draftRecipeContext.mealOrder,
    draftRecipeContext.recipeId,
    safeQuantity,
  );
  if (!response.success) {
    throw new Error(response.message || 'Failed to update quantity');
  }
}
